import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Chat from '../components/Chat';
import { searchListings, ChatMessage } from '../services/api';
import { saveSearch, isAuthenticated as checkAuth, getLifestyleToggle } from '../services/authService';

interface HeroImageSettings {
  enabled: boolean;
  filename: string | null;
  opacity: number;
  blend_mode: string;
  blur: number;
}

export default function HomePage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [isSearching, setIsSearching] = useState(false);
  const [heroImage, setHeroImage] = useState<HeroImageSettings | null>(null);

  // Fetch hero image settings on mount
  useEffect(() => {
    fetch('/api/admin/hero-image-settings')
      .then(r => r.json())
      .then(data => {
        if (data.success && data.data?.enabled && data.data?.filename) {
          setHeroImage(data.data);
        }
      })
      .catch(() => { /* silently ignore */ });
  }, []);

  async function handleProfileComplete(profile: any, messages: ChatMessage[], searchTitle?: string) {
    setIsSearching(true);

    try {
      // Store profile and messages in sessionStorage
      sessionStorage.setItem('searchProfile', JSON.stringify(profile));
      sessionStorage.setItem('chatMessages', JSON.stringify(messages));

      // Run the search
      const results = await searchListings(profile);
      sessionStorage.setItem('searchResults', JSON.stringify(results));

      // Auto-save search to user profile if logged in
      if (isAuthenticated && checkAuth()) {
        try {
          // Use AI-generated title if available, otherwise fallback to simple canton/city
          const hard = profile?.hard || {};
          const fallbackName = [hard.preferred_canton, hard.preferred_city].filter(Boolean).join(' / ') || 'Meine Suche';
          const profileName = searchTitle || fallbackName;
          const allResults = [...(results.topMatches || []), ...(results.nearMisses || [])];

          console.log('[Search] Auto-saving to profile...', { profileName, resultCount: allResults.length });

          const saved = await saveSearch({
            profileName,
            chatMessages: messages,
            profile,
            results: allResults,
            aiProvider: 'openai',
            lifestyleEnabled: getLifestyleToggle(),
          });
          
          console.log('[Search] Auto-saved successfully! Search ID:', saved.search?.id);
          
          // Store the search ID in sessionStorage so we can link favorites to it
          sessionStorage.setItem('currentSearchId', saved.search?.id?.toString() || '');
        } catch (saveErr: any) {
          // Log detailed error but don't block the user
          console.error('[Search] Auto-save failed:', saveErr);
          alert(`Hinweis: Suche konnte nicht gespeichert werden.\n\nFehler: ${saveErr.message}\n\nIhre Suche läuft trotzdem weiter.`);
        }
      }

      // Navigate to results
      navigate('/results');
    } catch (err: any) {
      console.error('Search failed:', err);
      alert('Suche fehlgeschlagen: ' + (err.message || 'Unbekannter Fehler'));
    }

    setIsSearching(false);
  }

  return (
    <div className="home-page">
      {/* Background layers – span full page behind header & footer */}
      <div className="hero-orb hero-orb--1" />
      <div className="hero-orb hero-orb--2" />
      <div className="hero-orb hero-orb--3" />

      {/* Hero background image (admin-configurable) */}
      {heroImage && heroImage.enabled && heroImage.filename && (
        <div
          className="hero-bg-image"
          style={{
            backgroundImage: `url(/uploads/${heroImage.filename})`,
            opacity: heroImage.opacity / 100,
            mixBlendMode: heroImage.blend_mode as any,
            filter: heroImage.blur > 0 ? `blur(${heroImage.blur}px)` : undefined,
          }}
        />
      )}

      {/* Dot grid pattern overlay */}
      <div className="hero-grid-pattern" />

      {/* Hero – main content section */}
      <section className="hero">
        <div className="hero-content">
          {/* Headline with gradient text */}
          <h1 className="hero-title">
            Finden Sie Ihr<br />
            <span className="hero-title-gradient">perfektes Zuhause</span>
          </h1>
          <p className="hero-subtitle">
            Erzählen Sie unserer AI von Ihrem Leben – wir finden die passende Immobilie in der Schweiz. Kein Formular, kein Filter-Chaos. Einfach uns mitteilen.
          </p>

          {/* Chat Interview – glassmorphism card */}
          <div className="chat-section">
            <div className="chat-glow" />
            <Chat onProfileComplete={handleProfileComplete} />

            {isSearching && (
              <div className="search-loading">
                <div className="spinner" />
                <p>Suche wird durchgeführt...</p>
              </div>
            )}
          </div>

          {/* Floating stat badges */}
          <div className="hero-badges">
            <div className="hero-badge hero-badge--delay-1">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
              <span>12'000+ Objekte</span>
            </div>
            <div className="hero-badge hero-badge--delay-2">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <span>AI-gestütztes Matching</span>
            </div>
            <div className="hero-badge hero-badge--delay-3">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              <span>Ganze Schweiz</span>
            </div>
          </div>
        </div>
      </section>

      {/* How it works – 3 steps */}
      <section className="how-it-works">
        <h2 className="how-it-works-title">So funktioniert's</h2>
        <div className="how-it-works-steps">
          <div className="how-step">
            <div className="how-step-icon how-step-icon--chat">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            </div>
            <h3 className="how-step-title">1. Erzählen</h3>
            <p className="how-step-desc">Beschreiben Sie Ihren Lebensstil, Ihre Wünsche und Bedürfnisse – wie in einem Gespräch mit einem Berater.</p>
          </div>

          <div className="how-step-connector">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </div>

          <div className="how-step">
            <div className="how-step-icon how-step-icon--ai">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a4 4 0 0 1 4 4v1a4 4 0 0 1-8 0V6a4 4 0 0 1 4-4z"/><path d="M16 14H8a4 4 0 0 0-4 4v2h16v-2a4 4 0 0 0-4-4z"/><line x1="12" y1="11" x2="12" y2="14"/></svg>
            </div>
            <h3 className="how-step-title">2. AI analysiert</h3>
            <p className="how-step-desc">Unsere künstliche Intelligenz versteht Ihre Anforderungen und durchsucht tausende Objekte in Sekunden.</p>
          </div>

          <div className="how-step-connector">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </div>

          <div className="how-step">
            <div className="how-step-icon how-step-icon--match">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            </div>
            <h3 className="how-step-title">3. Top-Matches</h3>
            <p className="how-step-desc">Erhalten Sie sofort eine personalisierte Auswahl der besten Immobilien – mit Score und Erklärung.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
